import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Card,
  Grid,
  Stack,
  Alert,
  Button,
  Container,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import { opcijeDrzava, jeStandardnaDrzava } from '../../utils/drzave';

const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER_URL;
const LISTA_URL = '/dashboard/HuntersReportAll';

// Polja koja se mogu mijenjati i šalju se na server
const PRAZNA_FORMA = {
  ime: '',
  prezime: '',
  telefon: '',
  godiste: '',
  mjesto: '',
  drzava: '',
};

// Podaci iz localStorage (upisuje ih dugme Update u tabeli) - koriste se samo ako server ne vrati korisnika
function podaciIzLocalStorage() {
  const get = (k) => localStorage.getItem(k) ?? '';
  return {
    ime: get('ime'),
    prezime: get('prezime'),
    telefon: get('telefon'),
    godiste: get('godiste'),
    mjesto: get('mjesto'),
    drzava: get('drzava'),
    username: get('username'),
    datumregistracije: get('datumregistracije'),
  };
}

export default function Update() {
  const navigate = useNavigate();

  const [id] = useState(localStorage.getItem('id'));
  const [forma, setForma] = useState(PRAZNA_FORMA);
  const [username, setUsername] = useState('');
  const [datumregistracije, setDatumRegistracije] = useState('');

  const [ucitavanje, setUcitavanje] = useState(true);
  const [snimanje, setSnimanje] = useState(false);
  const [pokusanoSnimanje, setPokusanoSnimanje] = useState(false);
  const [greska, setGreska] = useState('');
  const [upozorenje, setUpozorenje] = useState('');

  // --- Učitavanje svježih podataka sa servera ---

  useEffect(() => {
    if (!id) {
      setUcitavanje(false);
      return;
    }

    const primijeni = (s) => {
      setForma({
        ime: s.ime ?? '',
        prezime: s.prezime ?? '',
        telefon: s.telefon ?? '',
        godiste: s.godiste ?? '',
        mjesto: s.mjesto ?? '',
        drzava: s.drzava ?? '',
      });
      setUsername(s.username ?? '');
      setDatumRegistracije(s.datumregistracije ?? '');
    };

    const ucitaj = async () => {
      try {
        const response = await axios.get(`${SERVER_URL}/subscribers/${id}`);
        // Backend može vratiti objekat ili niz s jednim elementom
        const s = Array.isArray(response.data) ? response.data[0] : response.data;
        if (!s || !s.id) throw new Error('Prazan odgovor');
        primijeni(s);
      } catch (err) {
        console.log('GET /subscribers/:id nije uspio, koristim localStorage', err);
        primijeni(podaciIzLocalStorage());
        setUpozorenje('Podaci nisu učitani sa servera, prikazani su podaci iz tabele. Mogu biti zastarjeli.');
      } finally {
        setUcitavanje(false);
      }
    };

    ucitaj();
  }, [id]);

  // --- Forma ---

  const promijeni = (polje) => (event) => {
    setForma((prev) => ({ ...prev, [polje]: event.target.value }));
  };

  const obaveznoPrazno = (polje) => pokusanoSnimanje && !forma[polje].trim();

  const handleSpasi = async () => {
    setPokusanoSnimanje(true);
    setGreska('');

    if (!forma.ime.trim() || !forma.prezime.trim() || !forma.drzava) {
      setGreska('Ime, prezime i država su obavezni.');
      return;
    }

    // Šalju se samo polja koja se mijenjaju ovdje; username i password se ne diraju
    const podaci = Object.fromEntries(Object.entries(forma).map(([k, v]) => [k, String(v).trim()]));

    setSnimanje(true);
    try {
      await axios.put(`${SERVER_URL}/subscribers/${id}`, podaci);
      navigate(LISTA_URL);
    } catch (err) {
      console.log('Greška pri snimanju', err);
      setGreska('Snimanje nije uspjelo. Provjerite vezu sa serverom i pokušajte ponovo.');
      setSnimanje(false);
    }
  };

  const handleOdustani = () => {
    navigate(LISTA_URL);
  };

  // --- Render ---

  if (!id) {
    return (
      <Container maxWidth={false}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Nije izabran korisnik za izmjenu. Otvorite korisnika preko dugmeta Update u listi.
        </Alert>
        <Button onClick={handleOdustani}>Nazad na listu</Button>
      </Container>
    );
  }

  return (
    <Container maxWidth={false}>
      <Typography variant="subtitle2" noWrap sx={{ mb: 1 }}>
        Izmjena podataka pretplatnika
      </Typography>

      <Card sx={{ p: 3 }}>
        {ucitavanje ? (
          <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 4 }}>
            <CircularProgress size={24} />
            <Typography variant="body2">Učitavam podatke korisnika...</Typography>
          </Stack>
        ) : (
          <>
            <Stack direction="row" spacing={4} sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Broj: <strong>{id}</strong>
              </Typography>
              {datumregistracije && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Registrovan: <strong>{datumregistracije}</strong>
                </Typography>
              )}
            </Stack>

            {upozorenje && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                {upozorenje}
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  label="Ime"
                  value={forma.ime}
                  onChange={promijeni('ime')}
                  error={obaveznoPrazno('ime')}
                  helperText={obaveznoPrazno('ime') ? 'Ime je obavezno' : ' '}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  label="Prezime"
                  value={forma.prezime}
                  onChange={promijeni('prezime')}
                  error={obaveznoPrazno('prezime')}
                  helperText={obaveznoPrazno('prezime') ? 'Prezime je obavezno' : ' '}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Username"
                  value={username}
                  InputProps={{ readOnly: true }}
                  helperText="Username se ne može mijenjati"
                  sx={{ '& .MuiInputBase-root': { bgcolor: 'action.hover' } }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Telefon"
                  value={forma.telefon}
                  onChange={promijeni('telefon')}
                  helperText=" "
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Godište"
                  value={forma.godiste}
                  onChange={promijeni('godiste')}
                  helperText=" "
                />
              </Grid>

              <Grid item xs={12} md={4} />

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Mjesto"
                  value={forma.mjesto}
                  onChange={promijeni('mjesto')}
                  helperText=" "
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  required
                  label="Država"
                  value={forma.drzava}
                  onChange={promijeni('drzava')}
                  error={obaveznoPrazno('drzava') || (!!forma.drzava && !jeStandardnaDrzava(forma.drzava))}
                  helperText={
                    obaveznoPrazno('drzava')
                      ? 'Država je obavezna'
                      : forma.drzava && !jeStandardnaDrzava(forma.drzava)
                      ? 'Nestandardna vrijednost iz baze, izaberite državu sa liste'
                      : ' '
                  }
                >
                  {opcijeDrzava(forma.drzava).map((d) => (
                    <MenuItem key={d} value={d}>
                      {jeStandardnaDrzava(d) ? d : `${d} (nestandardno)`}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {greska && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {greska}
              </Alert>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <Button variant="contained" onClick={handleSpasi} disabled={snimanje}>
                {snimanje ? 'Snimam...' : 'Spasi'}
              </Button>
              <Button onClick={handleOdustani} disabled={snimanje}>
                Odustani
              </Button>
            </Stack>
          </>
        )}
      </Card>
    </Container>
  );
}